// Universal data converters for survey specifications
// These functions convert survey data into formats needed by different components

/**
 * Converts survey data to table format for SurveyTable component
 * @param {Object} surveyData - The survey specification data
 * @returns {Array} Array of questions formatted for table display
 */
export const convertToTableFormat = (surveyData) => {
  if (!surveyData || !surveyData.questions) {
    return [];
  }

  return surveyData.questions.map(question => ({
    ...question,
    // Ensure all questions have consistent structure
    options: question.options || [],
    automation: question.automation || null,
    showIf: question.showIf || null,
    required: question.required !== undefined ? question.required : false,
    parentGrid: question.parentGrid || null,
    parentCheckbox: question.parentCheckbox || null
  }));
};

/**
 * Converts survey data to flow format for SurveyFlow component
 * @param {Object} surveyData - The survey specification data
 * @returns {Object} Object with nodes and edges for React Flow
 */
export const convertToFlowFormat = (surveyData) => {
  if (!surveyData || !surveyData.questions) {
    return { nodes: [], edges: [] };
  }

  const nodes = [];
  const edges = [];
  const questions = surveyData.questions;

  // Start node
  nodes.push({
    id: 'start',
    type: 'input',
    data: { label: 'START' },
    position: { x: 250, y: 0 },
    style: {
      background: '#10b981',
      color: 'white',
      fontWeight: 'bold',
    },
  });

  // Filter out sub-items for main flow
  const mainQuestions = questions.filter(q =>
    q.type !== 'grid_item' &&
    q.type !== 'checkbox_item' &&
    !(q.id && q.id.includes('_grid'))
  );

  // Add question nodes
  let yPosition = 100;
  mainQuestions.forEach((question) => {
    // Skip conditional individual questions in main flow
    if (question.showIf && question.type !== 'group') {
      return;
    }

    const childCount = questions.filter(q =>
      q.parentGrid === question.id ||
      q.parentCheckbox === question.id ||
      (q.id && q.id.startsWith(question.id + '.'))
    ).length;

    nodes.push({
      id: question.id,
      type: 'question',
      data: {
        ...question,
        childCount: childCount
      },
      position: { x: 250, y: yPosition },
    });

    yPosition += 120;
  });

  // Connect nodes with edges
  const questionNodes = nodes.filter(n => n.type === 'question');
  questionNodes.forEach((node, index) => {
    if (index === 0) {
      edges.push({
        id: `e-start-${node.id}`,
        source: 'start',
        target: node.id,
      });
    } else {
      edges.push({
        id: `e-${questionNodes[index - 1].id}-${node.id}`,
        source: questionNodes[index - 1].id,
        target: node.id,
      });
    }
  });

  // End node
  if (questionNodes.length > 0) {
    const lastQuestion = questionNodes[questionNodes.length - 1];
    const endY = lastQuestion.position.y + 120;

    nodes.push({
      id: 'end',
      type: 'output',
      data: { label: 'END' },
      position: { x: 250, y: endY },
      style: {
        background: '#ef4444',
        color: 'white',
        fontWeight: 'bold',
      },
    });

    edges.push({
      id: `e-${lastQuestion.id}-end`,
      source: lastQuestion.id,
      target: 'end',
    });
  }

  return { nodes, edges };
};

/**
 * Converts survey data to automation table format
 * @param {Object} surveyData - The survey specification data
 * @returns {Array} Array of automation rules formatted for table display
 */
export const convertToAutomationFormat = (surveyData) => {
  if (!surveyData || !surveyData.surveyAutomationRules) {
    return [];
  }

  return surveyData.surveyAutomationRules.map(rule => ({
    ...rule,
    // Ensure consistent structure
    questionId: Array.isArray(rule.questionId) ? rule.questionId : [rule.questionId],
    actions: rule.actions || [],
    condition: rule.condition || '',
    triggerCondition: rule.triggerCondition || ''
  }));
};

/**
 * Gets survey metadata for display
 * @param {Object} surveyData - The survey specification data
 * @returns {Object} Survey metadata
 */
export const getSurveyMetadata = (surveyData) => {
  if (!surveyData) {
    return {};
  }

  return {
    title: surveyData.surveyInfo?.title || 'Unknown Survey',
    version: surveyData.surveyInfo?.version || '1.0.0',
    lastUpdated: surveyData.surveyInfo?.lastUpdated || new Date().toISOString(),
    totalQuestions: surveyData.surveyInfo?.totalQuestions || surveyData.questions?.length || 0,
    estimatedTime: surveyData.surveyInfo?.estimatedTime || '5-10 minutes',
    description: surveyData.surveyInfo?.description || '',
    questionTypes: surveyData.questionTypes || [],
    automationSummary: surveyData.automationSummary || {}
  };
};

/**
 * Analyzes survey data to determine effective conditions for grouped questions
 * This handles cases where questions belong to conditional groups but don't have direct showIf
 * @param {Object} surveyData - The survey specification data
 * @returns {Object} Map of questionId to effective showIf condition
 */
export const analyzeConditionalGroups = (surveyData) => {
  const effectiveConditions = {};
  
  if (!surveyData || !surveyData.questions) {
    return effectiveConditions;
  }

  const questions = surveyData.questions;
  
  // Find conditional groups - groups that have showIf conditions
  const conditionalGroups = questions.filter(q => q.type === 'group' && q.showIf);
  
  conditionalGroups.forEach(group => {
    // Find all questions that should inherit this group's condition
    const groupCondition = group.showIf;
    
    // Look for questions that immediately follow this group
    const groupIndex = questions.findIndex(q => q.id === group.id);
    
    // Scan forward from the group to find related questions
    for (let i = groupIndex + 1; i < questions.length; i++) {
      const question = questions[i];
      
      // Stop when we hit another group (unless it's a conditional group with the same condition)
      if (question.type === 'group') {
        if (question.showIf !== groupCondition) {
          break;
        }
        // If it's another group with the same condition, continue scanning
        continue;
      }
      
      // If question already has its own showIf, don't override it
      if (question.showIf) {
        continue;
      }
      
      // Add this question to the group's condition
      effectiveConditions[question.id] = groupCondition;
      
      // Also handle child questions (grid items, etc.)
      const childQuestions = questions.filter(q => 
        q.parentGrid === question.id || 
        q.parentCheckbox === question.id ||
        (q.id && q.id.startsWith(question.id + '.'))
      );
      
      childQuestions.forEach(child => {
        if (!child.showIf) {
          effectiveConditions[child.id] = groupCondition;
        }
      });
    }
  });
  
  return effectiveConditions;
};

/**
 * Gets the effective condition for a question, considering group inheritance
 * @param {Object} question - The question object
 * @param {Object} effectiveConditions - Map from analyzeConditionalGroups
 * @returns {string|null} The effective showIf condition
 */
export const getEffectiveCondition = (question, effectiveConditions) => {
  // Direct condition has priority
  if (question.showIf) {
    return question.showIf;
  }
  
  // Check if question inherits condition from a group
  if (effectiveConditions && effectiveConditions[question.id]) {
    return effectiveConditions[question.id];
  }
  
  return null;
};

/**
 * Validates survey data structure
 * @param {Object} surveyData - The survey specification data
 * @returns {Object} Validation result with isValid boolean and errors array
 */
export const validateSurveyData = (surveyData) => {
  const errors = [];

  if (!surveyData) {
    errors.push('Survey data is missing');
    return { isValid: false, errors };
  }

  if (!surveyData.questions || !Array.isArray(surveyData.questions)) {
    errors.push('Questions array is missing or invalid');
  }

  if (!surveyData.surveyInfo) {
    errors.push('Survey info is missing');
  }

  // Check question structure
  if (surveyData.questions) {
    surveyData.questions.forEach((question, index) => {
      if (!question.id) {
        errors.push(`Question at index ${index} is missing id`);
      }
      if (!question.type) {
        errors.push(`Question ${question.id || index} is missing type`);
      }
      if (!question.text) {
        errors.push(`Question ${question.id || index} is missing text`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors
  };
};
